import { Component, ViewChild, OnInit, OnDestroy, HostListener } from '@angular/core';
import { BcFobPackagesComponent } from './bc-fob-packages/bc-fob-packages/bc-fob-packages.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BcFobModificationComponent } from '../bc-fob-modification/bc-fob-modification.component';
import { BcFobModelInputsComponent } from './bc-fob-model-inputs/bc-fob-model-inputs.component';
import { SharedServiceService } from '../../services/shared-service.service';
import { AllServicesService } from '../../../services/all-services.service';
import { APP_ERROR_CODES, getUserMessage, mapUnknownError } from '../../services/app-error-catalog';
import { createAppError } from '../../services/app-error.model';
import { LoggerService } from '../../services/logger.service';
import { NotificationFacadeService } from '../../services/notification-facade.service';
import { ModelInputsComputationService, ModelInputsViewData } from '../../services/model-inputs-computation.service';
import { applyRevisedShearedKv100, ShearedPredictionRow } from '../../services/revised-sheared-kv100';
import { Subscription, Observable, forkJoin } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FavoriteFacadeService } from '../../services/favorites/favorite-facade.service';
import { BcWeightCalculatorComponent, WeightCalculatorContext } from '../bc-weight-calculator/bc-weight-calculator.component';

@Component({
  selector: 'app-bc-finished-oil-blend',
  imports: [BcFobModificationComponent, BcFobPackagesComponent,
    CommonModule, FormsModule, BcFobPackagesComponent,
    BcFobModelInputsComponent, BcWeightCalculatorComponent],
  templateUrl: './bc-finished-oil-blend.component.html',
  styleUrl: './bc-finished-oil-blend.component.scss'
})

/**
 * Orchestrates the left panel of Blend Calculator:
 * - Modification section (base oils, VM, PPD, packages)
 * - Model Inputs modals per blend
 * - Direct prediction execution path (without opening modal)
 *
 * This component does not own table internals; it validates readiness,
 * builds backend payloads, and coordinates calls/results through services.
 */
export class BcFinishedOilBlendComponent implements OnInit, OnDestroy {
  @ViewChild('modificationComponent', { static: false }) modificationComponent!: BcFobModificationComponent;
  @ViewChild('packageComponent', { static: false }) packageComponent!: BcFobPackagesComponent;
  selectedFinishedOilOption = "modifications";
  isModal1Open = false;
  isModal2Open = false;
  isWeightCalculatorOpen = false;
  weightCalculatorContext: WeightCalculatorContext = {};
  modalData: any[] = [];
  currentPackageData: any[] = [];
  vmData: any[] = []; // Data for Viscosity Modifier

  // Data for Execute Prediction button validation
  baseOilData: any[] = [];
  vmSelectedData: any[] = [];
  ppdData: any[] = [];
  packagesData: any[] = [];
  retrievedPackageData: any[] = [];

  // Package-level treat wt% totals in FO, indexed by package: p1 -> Prediction 1, p2 -> Prediction 2
  private packageTreatTotals: { p1: number[]; p2: number[] } = { p1: [0, 0], p2: [0, 0] };

  // Button state
  canExecutePrediction: boolean = false;
  isExecutingPrediction: boolean = false;
  private readonly ratioTotalTolerance = 0.0001;

  // Auto-execute prediction after import
  private autoExecuteOnReady = false;
  private autoExecuteTimer: any = null;

  // Save favorite dialog
  showSaveDialog = false;
  saveFavoriteName = '';
  saveError = '';
  private importedName: string | null = null;
  private importedSlot: string | null = null;
  private importedFilename: string | null = null;
  isSaving = false;

  // Overwrite confirmation (name already taken)
  showOverwriteConfirm = false;
  pendingOverwrite: { slotNum: number; filename: string | null; name: string } | null = null;

  private subscriptions: Subscription[] = [];

  constructor(
    private dataService: AllServicesService,
    private sharedService: SharedServiceService,
    private logger: LoggerService,
    private notificationFacade: NotificationFacadeService,
    private miService: ModelInputsComputationService,
    private favoriteFacade: FavoriteFacadeService) {
  }

  ngOnInit(): void {
    // Subscribe to child component data for real-time validation
    this.sharedService.baseOilSelectedData$.subscribe(data => {
      this.baseOilData = data;
      this.validatePredictionReady();
    });

    this.sharedService.vmSelectedData$.subscribe(data => {
      this.vmSelectedData = data;
      this.validatePredictionReady();
    });

    this.sharedService.ppdSelectedData$.subscribe(data => {
      this.ppdData = data;
      this.validatePredictionReady();
    });

    this.sharedService.packagesData$.subscribe(data => {
      this.packagesData = data;
      this.validatePredictionReady();
    });

    // Also subscribe to retrievedPackageData$ to detect when packages are cleared
    this.sharedService.retrievedPackageData$.subscribe(data => {
      this.retrievedPackageData = data;
      this.validatePredictionReady();
    });

    // Track package-level treat wt% totals so the Weight Calculator can show the
    // selected package (not its additives) with its proper treat wt% per prediction.
    this.sharedService.total_package_1_pred_1$.subscribe(v => this.packageTreatTotals.p1[0] = Number(v) || 0);
    this.sharedService.total_package_1_pred_2$.subscribe(v => this.packageTreatTotals.p2[0] = Number(v) || 0);
    this.sharedService.total_package_2_pred_1$.subscribe(v => this.packageTreatTotals.p1[1] = Number(v) || 0);
    this.sharedService.total_package_2_pred_2$.subscribe(v => this.packageTreatTotals.p2[1] = Number(v) || 0);

    // Auto-execute prediction when a favorite is imported
    this.subscriptions.push(
      this.sharedService.ImpFavoriteFlag$.pipe(
        filter(flag => flag === true)
      ).subscribe(() => {
        this.autoExecuteOnReady = true;
      })
    );

    // Track imported favorite metadata for save dialog
    this.subscriptions.push(
      this.sharedService.importedFavoriteName$.subscribe(n => this.importedName = n),
      this.sharedService.importedFavoriteSlot$.subscribe(s => this.importedSlot = s),
      this.sharedService.importedFavoriteFilename$.subscribe(f => this.importedFilename = f),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.autoExecuteTimer) clearTimeout(this.autoExecuteTimer);
  }

  // #region Save Favorite dialog

  openSaveDialog(): void {
    this.saveFavoriteName = this.importedName || '';
    this.saveError = '';
    this.showSaveDialog = true;
  }

  confirmSaveFavorite(): void {
    const name = this.saveFavoriteName.trim();
    if (!name || this.isSaving) return;

    // Detect an existing favorite (any slot) using the same sanitized name.
    const slots = this.favoriteFacade.getSlotsSnapshot();
    const existingSlot = this.favoriteFacade.findOccupiedSlotByName(name);

    if (existingSlot) {
      // Name taken -> ask for confirmation before overwriting.
      this.pendingOverwrite = {
        slotNum: parseInt(existingSlot.slno, 10),
        filename: existingSlot.Filename,
        name,
      };
      this.showOverwriteConfirm = true;
      return;
    }

    this.saveError = '';
    this.isSaving = true;

    // New: find next available slot
    const emptySlot = slots.find(s => s.Name === null);
    const slotNum = emptySlot ? parseInt(emptySlot.slno, 10) : slots.length + 1;
    this.doSaveToSlot(slotNum, name);
  }

  onSaveNameChange(value: string): void {
    this.saveFavoriteName = this.favoriteFacade.stripForbiddenNameChars(value);
    this.saveError = '';
  }

  confirmOverwrite(): void {
    if (!this.pendingOverwrite || this.isSaving) return;
    const { slotNum, filename, name } = this.pendingOverwrite;
    this.showOverwriteConfirm = false;
    this.saveError = '';
    this.isSaving = true;

    if (filename) {
      // Overwrite: delete existing blob, then save to the same slot.
      this.dataService.delete_Favorites_in_blob({ file_Name: filename }).subscribe({
        next: () => this.doSaveToSlot(slotNum, name),
        error: () => {
          this.isSaving = false;
          this.showSaveDialog = false;
          this.pendingOverwrite = null;
        }
      });
    } else {
      this.doSaveToSlot(slotNum, name);
    }
  }

  cancelOverwrite(): void {
    this.showOverwriteConfirm = false;
    this.pendingOverwrite = null;
  }

  private doSaveToSlot(slotNum: number, name: string): void {
    this.favoriteFacade.saveFavoriteWithName(slotNum, name).subscribe({
      next: (result: any) => {
        this.isSaving = false;
        this.showSaveDialog = false;
        this.pendingOverwrite = null;
        if (result.success) {
          // Update local cache with actual payload so it can be imported without reload
          this.favoriteFacade.addSlotLocally(String(slotNum), name, result.payload || '');
          // Update imported metadata to reflect saved state
          this.sharedService.setImportedFavoriteName(name);
          this.sharedService.setImportedFavoriteSlot(String(slotNum));
          this.sharedService.setImportedFavoriteFilename(this.favoriteFacade.buildFilename(name));
        }
      },
      error: () => {
        this.isSaving = false;
        this.showSaveDialog = false;
        this.pendingOverwrite = null;
      }
    });
  }

  cancelSaveDialog(): void {
    if (this.isSaving) return; // Block closing while a save is in progress
    this.showSaveDialog = false;
  }

  // #endregion Save Favorite

  // #region Prediction readiness and execution

  // Real-time validation: enable/disable Execute Prediction button
  private validatePredictionReady(): void {
    // Minimum requirement 1: At least one package with components
    // Check both packagesData and retrievedPackageData
    const hasValidPackages = this.packagesData && this.packagesData.length > 0 &&
      this.packagesData.some((pkg: any) => {
        // Check that pkg is not null/undefined
        if (!pkg || pkg === null) {
          return false;
        }
        // pkg is [blend0[], blend1[]] - verify that at least one blend has data
        if (Array.isArray(pkg)) {
          return pkg.some((blend: any) => Array.isArray(blend) && blend.length > 0);
        }
        return false;
      });

    // Minimum requirement 2: At least one Base Oil with a name, ratio > 0, and selected batch (per blend)
    const hasBlend1BaseOil = this.baseOilData && this.baseOilData.some((b: any) => {
      const name = b.oil || b.name;
      const batch = b.batch || b.Batch;
      const ratio = Number(b.ratio1) || 0;
      return name && name.trim() !== '' && batch && String(batch).trim() !== '' && ratio > 0;
    });
    const hasBlend2BaseOil = this.baseOilData && this.baseOilData.some((b: any) => {
      const name = b.oil || b.name;
      const batch = b.batch || b.Batch;
      const ratio = Number(b.ratio2) || 0;
      return name && name.trim() !== '' && batch && String(batch).trim() !== '' && ratio > 0;
    });

    const hasBlend1MissingBatch = this.baseOilData && this.baseOilData.some((b: any) => {
      const ratio = Number(b.ratio1) || 0;
      if (ratio <= 0) {
        return false;
      }
      const batch = b.batch || b.Batch;
      return !batch || String(batch).trim() === '';
    });
    const hasBlend2MissingBatch = this.baseOilData && this.baseOilData.some((b: any) => {
      const ratio = Number(b.ratio2) || 0;
      if (ratio <= 0) {
        return false;
      }
      const batch = b.batch || b.Batch;
      return !batch || String(batch).trim() === '';
    });

    const blend1RatioComplete = hasBlend1BaseOil && !hasBlend1MissingBatch && this.isBlendRatioTotalComplete(1);
    const blend2RatioComplete = hasBlend2BaseOil && !hasBlend2MissingBatch && this.isBlendRatioTotalComplete(2);
    const hasReadyBlend = blend1RatioComplete || blend2RatioComplete;

    this.canExecutePrediction = hasReadyBlend && hasValidPackages;

    // Auto-execute prediction after favorite import
    if (this.autoExecuteOnReady && this.canExecutePrediction && !this.isExecutingPrediction) {
      if (this.autoExecuteTimer) clearTimeout(this.autoExecuteTimer);
      this.autoExecuteTimer = setTimeout(() => {
        this.autoExecuteOnReady = false;
        this.autoExecuteTimer = null;
        if (this.canExecutePrediction && !this.isExecutingPrediction) {
          this.executePrediction();
        }
      }, 500);
    }
  }

  private isBlendRatioTotalComplete(blendNumber: number): boolean {
    const totals = this.sharedService.getAllComponentTotals();
    const ratioTotal = blendNumber === 1
      ? Number(totals?.baseOil?.ratioP1) || 0
      : Number(totals?.baseOil?.ratioP2) || 0;

    return Math.abs(ratioTotal - 100) < this.ratioTotalTolerance;
  }

  /**
   * Checks whether a specific blend has valid data for prediction
   * @param blendNumber 1 for Blend 1, 2 for Blend 2
   */
  private hasValidBlendData(blendNumber: number): boolean {
    // Check that there is at least one Base Oil with a valid ratio for this blend
    const ratioKey = blendNumber === 1 ? 'ratio1' : 'ratio2';
    const hasBaseOil = this.baseOilData && this.baseOilData.some((b: any) => {
      const ratio = Number(b[ratioKey]) || 0;
      return ratio > 0;
    });

    if (!hasBaseOil) {
      return false;
    }

    const hasMissingBatch = this.baseOilData && this.baseOilData.some((b: any) => {
      const ratio = Number(b[ratioKey]) || 0;
      if (ratio <= 0) {
        return false;
      }
      const batch = b.batch || b.Batch;
      return !batch || String(batch).trim() === '';
    });

    if (hasMissingBatch) {
      return false;
    }

    if (!this.isBlendRatioTotalComplete(blendNumber)) {
      return false;
    }

    // A blend without any package additive treat must not be predicted
    if (!this.hasValidPackageTreat(blendNumber)) {
      return false;
    }

    return true;
  }

  /**
   * Checks whether a blend has at least one package additive with a non-zero treat.
   * @param blendNumber 1 for Blend 1, 2 for Blend 2
   */
  private hasValidPackageTreat(blendNumber: number): boolean {
    if (!this.packagesData || this.packagesData.length === 0) {
      return false;
    }

    const blendIndex = blendNumber - 1; // 1->0, 2->1

    return this.packagesData.some((pkg: any) => {
      const blendData = Array.isArray(pkg) && pkg.length > blendIndex ? pkg[blendIndex] : [];
      if (!Array.isArray(blendData)) {
        return false;
      }

      return blendData.some((item: any) => {
        if (!item || !item.component) {
          return false;
        }
        const rawTreatRate = Number.isFinite(item.treatRateRaw)
          ? item.treatRateRaw
          : parseFloat(item.treatRate);
        return Number.isFinite(rawTreatRate) && rawTreatRate > 0;
      });
    });
  }

  private addPredNumIfMissing(items: any[], predNum: number): any[] {
    return (items || []).map(item => {
      if (!item || item.pred_num !== undefined) {
        return item;
      }
      return { ...item, pred_num: predNum };
    });
  }

  /**
   * Business rejection: backend answered successfully but the blend still cannot
   * produce a prediction with the current model input state.
   */
  private showPredictionRejected(blendNumber: number): void {
    const error = createAppError({
      category: 'business',
      code: APP_ERROR_CODES.PREDICTION_REJECTED,
      source: 'executePrediction',
      severity: 'warning',
      userMessage: getUserMessage(APP_ERROR_CODES.PREDICTION_REJECTED),
      technicalMessage: `Prediction rejected for blend ${blendNumber}`,
      details: { blendNumber }
    });

    this.logger.warn('Prediction rejected by business rule', {
      blendNumber,
      code: error.code
    });
    this.notificationFacade.showError(error);
  }

  /**
   * Transport/runtime failures that happen after the request is sent are normalized
   * here so executePrediction keeps a single user-facing path for technical errors.
   */
  private handlePredictionRequestError(error: unknown, blendNumber: number): void {
    const appError = mapUnknownError(error, 'executePrediction');
    this.logger.error(appError, {
      blendNumber,
      operation: 'executePrediction'
    });
    this.notificationFacade.showError(appError);
  }

  /**
   * Local failures while building the request payload are mapped separately from
   * HTTP failures because no backend call was completed yet.
   */
  private handlePredictionBuildError(error: unknown): void {
    const baseError = mapUnknownError(error, 'buildDirectPredictionPayload');
    const appError = createAppError({
      category: 'design',
      code: APP_ERROR_CODES.PREDICTION_REQUEST_BUILD_FAILED,
      source: 'buildDirectPredictionPayload',
      userMessage: getUserMessage(APP_ERROR_CODES.PREDICTION_REQUEST_BUILD_FAILED),
      technicalMessage: baseError.technicalMessage,
      originalError: error,
      details: baseError.details
    });

    this.logger.error(appError, { operation: 'buildDirectPredictionPayload' });
    this.notificationFacade.showError(appError);
  }

  // Direct prediction without going through Model Inputs (equivalent to React's get_predictions)
  executePrediction(): void {
    if (!this.canExecutePrediction || this.isExecutingPrediction) {
      return;
    }

    // Check which blends have valid data
    const hasBlend1Data = this.hasValidBlendData(1);
    const hasBlend2Data = this.hasValidBlendData(2);

    if (!hasBlend1Data && !hasBlend2Data) {
      return;
    }

    const requestedFormulationSignature = this.getCurrentFormulationSignature();
    this.isExecutingPrediction = true;

    // Derive Model Inputs up-front for the blends with valid data.
    // deriveAndPersist persists to SharedService and preserves user-edited saved values,
    // so a cold prediction (modal never opened) still sends the derived mod_inp_* payload.
    const derivations: Observable<ModelInputsViewData | null>[] = [];
    if (hasBlend1Data) {
      derivations.push(this.miService.deriveAndPersist(1, 'prediction-build'));
    }
    if (hasBlend2Data) {
      derivations.push(this.miService.deriveAndPersist(2, 'prediction-build'));
    }

    forkJoin(derivations).subscribe({
      next: () => {
        this.runPredictionChain(
          hasBlend1Data,
          hasBlend2Data,
          requestedFormulationSignature,
          this.getModelInputsSignature()
        );
      },
      error: (error) => {
        // Abort prediction if Model Inputs derivation fails; the payload would be incomplete.
        this.isExecutingPrediction = false;
        this.handlePredictionBuildError(error);
      }
    });
  }

  // Runs the prediction request chain once Model Inputs are derived and persisted.
  private runPredictionChain(
    hasBlend1Data: boolean,
    hasBlend2Data: boolean,
    requestedFormulationSignature: string,
    requestedModelInputsSignature: string,
  ): void {
    try {
      const predictions: any[] = [];

      // Run prediction for Blend 1 if it has data
      if (hasBlend1Data) {
        const payload1 = this.buildDirectPredictionPayload(1);

        this.dataService.get_predictions(payload1).subscribe({
          next: (response1) => {

            if (response1[1] === 'false') {
              this.isExecutingPrediction = false;
              this.showPredictionRejected(1);
              return;
            }

            const revisedResponse1 = this.applyRevisedShearedResults(1, response1[0]);
            predictions.push(...this.addPredNumIfMissing(revisedResponse1, 1));

            // If Blend 2 also exists, run that prediction too
            if (hasBlend2Data) {
              const payload2 = this.buildDirectPredictionPayload(2);

              this.dataService.get_predictions(payload2).subscribe({
                next: (response2) => {
                  this.isExecutingPrediction = false;

                  if (response2[1] === 'false') {
                    this.showPredictionRejected(2);
                    return;
                  }

                  const revisedResponse2 = this.applyRevisedShearedResults(2, response2[0]);
                  predictions.push(...this.addPredNumIfMissing(revisedResponse2, 2));
                  this.publishPredictionResults(
                    predictions,
                    requestedFormulationSignature,
                    requestedModelInputsSignature
                  );
                },
                error: (err) => {
                  this.isExecutingPrediction = false;
                  this.handlePredictionRequestError(err, 2);
                }
              });
            } else {
              // Blend 1 only
              this.isExecutingPrediction = false;
              this.publishPredictionResults(
                predictions,
                requestedFormulationSignature,
                requestedModelInputsSignature
              );
            }
          },
          error: (err) => {
            this.isExecutingPrediction = false;
            this.handlePredictionRequestError(err, 1);
          }
        });
      } else if (hasBlend2Data) {
        // Blend 2 only
        const payload2 = this.buildDirectPredictionPayload(2);

        this.dataService.get_predictions(payload2).subscribe({
          next: (response2) => {
            this.isExecutingPrediction = false;

            if (response2[1] === 'false') {
              this.showPredictionRejected(2);
              return;
            }

            const revisedResponse2 = this.applyRevisedShearedResults(2, response2[0]);
            predictions.push(...this.addPredNumIfMissing(revisedResponse2, 2));
            this.publishPredictionResults(
              predictions,
              requestedFormulationSignature,
              requestedModelInputsSignature
            );
          },
          error: (err) => {
            this.isExecutingPrediction = false;
            this.handlePredictionRequestError(err, 2);
          }
        });
      }

    } catch (error) {
      this.isExecutingPrediction = false;
      this.handlePredictionBuildError(error);
    }
  }

  // Build payload for get_predictions (simplified version vs Model Inputs).
  // Keep one-blend-per-call contract to mirror legacy React implementation.
  private buildDirectPredictionPayload(blendNumber: number): any {

    const reconcileSummary = this.sharedService.reconcileModelInputsForBlend(blendNumber, 'prediction-build');

    const consistencyReport = this.sharedService.validateModelInputsConsistencyForBlend(blendNumber);

    // IMPORTANT: get_predictions uses DIFFERENT names than get_Model_Inputs
    // React uses: vm_data_additive, vm_data_Treat_wt, bo_inp_Base_oil, etc.

    const blendIndex = blendNumber - 1; // 1->0, 2->1

    // 1. Process VM data - ONLY FOR THE SPECIFIED BLEND
    const vm_data_additive: any[] = [];
    const vm_data_Treat_wt: any[] = [];

    if (this.vmSelectedData && this.vmSelectedData.length > 0) {
      this.vmSelectedData.forEach((vm: any) => {
        // IMPORTANT: Use the treat specific to the blend being predicted
        const treatKey = blendNumber === 1 ? 'treat1' : 'treat2';
        const treatValue = vm[treatKey];

        if (vm.selectedValue && treatValue && treatValue !== '0') {
          vm_data_additive.push(vm.selectedValue);
          vm_data_Treat_wt.push(parseFloat(treatValue));
        } else {
          vm_data_additive.push('0');
          vm_data_Treat_wt.push(parseFloat(null as any)); // NaN
        }
      });
    }

    // If there is no VM, send the React pattern (at least 2 entries)
    if (vm_data_additive.length === 0) {
      vm_data_additive.push('0', '0');
      vm_data_Treat_wt.push(parseFloat(null as any), parseFloat(null as any));
    }

    // 2. Process PPD data - ONLY FOR THE SPECIFIED BLEND
    const ppd_data_additive: any[] = [];
    const ppd_data_Treat_wt: any[] = [];

    if (this.ppdData && this.ppdData.length > 0) {
      this.ppdData.forEach((ppd: any) => {
        const treatKey = blendNumber === 1 ? 'treat1' : 'treat2';
        const selectedName = ppd.ppd ?? ppd.selectedValue ?? ppd.name ?? '';
        const treatValue = ppd[treatKey];

        if (selectedName && treatValue && treatValue !== '0') {
          ppd_data_additive.push(selectedName);
          ppd_data_Treat_wt.push(parseFloat(treatValue));
        } else {
          ppd_data_additive.push('0');
          ppd_data_Treat_wt.push(parseFloat(null as any));
        }
      });
    }

    if (ppd_data_additive.length === 0) {
      ppd_data_additive.push('0', '0');
      ppd_data_Treat_wt.push(parseFloat(null as any), parseFloat(null as any));
    }

    // 3. Process Base Oil data - ONLY FOR THE SPECIFIED BLEND
    const bo_inp_Base_oil: any[] = [];
    const bo_inp_batch: any[] = [];
    const bo_inp_Treat_wt_ratio: any[] = [];

    if (this.baseOilData && this.baseOilData.length > 0) {
      this.baseOilData.forEach((bo: any) => {
        const name = bo.oil ?? bo.name;
        const batch = bo.batch ?? '';
        // IMPORTANT: React validates both treat_wt and ratio
        const treatKey = blendNumber === 1 ? 'treat1' : 'treat2';
        const ratioKey = blendNumber === 1 ? 'ratio1' : 'ratio2';
        const treatValue = bo[treatKey];
        const ratio = bo[ratioKey];

        // React incluye el BO si treat_wt no es null Y ratio existe
        if (name && treatValue && treatValue !== '0' && ratio && ratio !== '0') {
          bo_inp_Base_oil.push(name);
          bo_inp_batch.push(batch);
          bo_inp_Treat_wt_ratio.push(parseFloat(ratio));
        } else {
          bo_inp_Base_oil.push(null);
          bo_inp_batch.push(null);
          bo_inp_Treat_wt_ratio.push(parseFloat(null as any));
        }
      });
    }

    // 4. Process DI Package data - ONLY FOR THE SPECIFIED BLEND
    const di_inp_package_Num: any[] = [];
    const di_inp_additive: any[] = [];
    const di_inp_dossage: any[] = [];
    const dil_package_Num: any[] = [];
    const dil_Treat_wt_package: any[] = [];

    if (this.packagesData && this.packagesData.length > 0) {
      this.packagesData.forEach((pkg: any, pkgIndex: number) => {
        // packagesData structure: [[pkg1_blend1, pkg1_blend2], [pkg2_blend1, pkg2_blend2]]
        // Use the specified blendIndex (0 or 1)
        const blendIndex = blendNumber - 1; // 1->0, 2->1
        const blendData = Array.isArray(pkg) && pkg.length > blendIndex ? pkg[blendIndex] : [];

        // IMPORTANT: React validates that the package has a valid treat_wt
        // Include only if there is data and the package has a valid treat
        if (Array.isArray(blendData) && blendData.length > 0) {
          blendData.forEach((item: any) => {
            if (item.component) {
              di_inp_package_Num.push(pkgIndex + 1); // Package numbers: 1, 2
              di_inp_additive.push(item.component);
              const rawTreatRate = Number.isFinite(item.treatRateRaw)
                ? item.treatRateRaw
                : parseFloat(item.treatRate);
              di_inp_dossage.push(Number.isFinite(rawTreatRate) ? rawTreatRate : 0);
            }
          });

          // dil data: one entry per package
          dil_package_Num.push(pkgIndex + 1);
          // Use the dilution of the specified blend from SharedService
          const dilForBlend = this.sharedService.getTotalDilPerPackage(pkgIndex, blendIndex);
          dil_Treat_wt_package.push(isNaN(dilForBlend) || dilForBlend === 0 ? 0 : dilForBlend);
        }
      });
    }

    // 5. Build mod_inp_* arrays from SharedService (values edited in Model Inputs)
    // IMPORTANT: isolate by active blend to avoid mixing payload between predictions.
    const activeModelInputs = blendNumber === 1
      ? {
        vm: this.sharedService.get_mod_inp_vm_value() || [],
        bo: this.sharedService.get_mod_inp_bo_value() || [],
        di: this.sharedService.get_mod_inp_di_value() || []
      }
      : {
        vm: this.sharedService.get_mod_inp_vm2_value() || [],
        bo: this.sharedService.get_mod_inp_bo2_value() || [],
        di: this.sharedService.get_mod_inp_di2_value() || []
      };

    const activeCols = this.sharedService.getModelInputColumnsFromData(
      activeModelInputs.vm,
      activeModelInputs.bo,
      activeModelInputs.di
    );

    const mod_inp_vm = blendNumber === 1 ? activeModelInputs.vm : [];
    const mod_inp_bo = blendNumber === 1 ? activeModelInputs.bo : [];
    const mod_inp_di = blendNumber === 1 ? activeModelInputs.di : [];
    const mod_inp_ppd: any[] = []; // PPD not currently used
    const mod_inp_col_vm = blendNumber === 1 ? activeCols.vm : [];
    const mod_inp_col_bo = blendNumber === 1 ? activeCols.bo : [];
    const mod_inp_col_di = blendNumber === 1 ? activeCols.di : [];

    // Pred 2 arrays with same logic, only activating if blendNumber === 2
    const mod_inp_vm2 = blendNumber === 2 ? activeModelInputs.vm : [];
    const mod_inp_bo2 = blendNumber === 2 ? activeModelInputs.bo : [];
    const mod_inp_di2 = blendNumber === 2 ? activeModelInputs.di : [];
    const mod_inp_ppd2: any[] = []; // PPD not currently used
    const mod_inp_col_vm2 = blendNumber === 2 ? activeCols.vm : [];
    const mod_inp_col_bo2 = blendNumber === 2 ? activeCols.bo : [];
    const mod_inp_col_di2 = blendNumber === 2 ? activeCols.di : [];

    // 6. Build final payload (React format - ONE BLEND AT A TIME)
    const payload = {
      data: {
        // Main fields for prediction - ONE BLEND ONLY
        pred_Num: [blendNumber], // Only 1 or only 2
        vm_data_additive: vm_data_additive,
        vm_data_Treat_wt: vm_data_Treat_wt,
        ppd_data_additive: ppd_data_additive,
        ppd_data_Treat_wt: ppd_data_Treat_wt,
        bo_inp_Base_oil: bo_inp_Base_oil,
        bo_inp_batch: bo_inp_batch,
        bo_inp_Treat_wt_ratio: bo_inp_Treat_wt_ratio,
        di_inp_package_Num: di_inp_package_Num,
        di_inp_additive: di_inp_additive,
        di_inp_dossage: di_inp_dossage,
        dil_package_Num: dil_package_Num,
        dil_Treat_wt_package: dil_Treat_wt_package,

        // mod_inp_* fields (required by the backend even if they are empty)
        mod_inp_vm: mod_inp_vm,
        mod_inp_di: mod_inp_di,
        mod_inp_ppd: mod_inp_ppd,
        mod_inp_bo: mod_inp_bo,
        mod_inp_col_vm: mod_inp_col_vm,
        mod_inp_col_di: mod_inp_col_di,
        mod_inp_col_bo: mod_inp_col_bo,

        // Pred 2 (empty)
        mod_inp_vm2: mod_inp_vm2,
        mod_inp_di2: mod_inp_di2,
        mod_inp_ppd2: mod_inp_ppd2,
        mod_inp_bo2: mod_inp_bo2,
        mod_inp_col_vm2: mod_inp_col_vm2,
        mod_inp_col_di2: mod_inp_col_di2,
        mod_inp_col_bo2: mod_inp_col_bo2
      }
    };

    return payload;
  }

  // #endregion

  onRadioButtonChange($event: { target: { value: string; }; }) {
    this.selectedFinishedOilOption = $event.target.value;
  }

  openModal1() {
    // Only opens the modal; loading is handled by the child component
    this.isModal1Open = true;
  }

  openWeightCalculator(): void {
    this.weightCalculatorContext = {
      ...this.weightCalculatorContext,
      finishedOil: {
        baseOilData: this.baseOilData,
        vmSelectedData: this.vmSelectedData,
        ppdData: this.ppdData,
        packages: this.buildFinishedOilPackages(),
      },
    };
    this.isWeightCalculatorOpen = true;
  }

  private buildFinishedOilPackages(): { name: string; treat1: number | null; treat2: number | null }[] {
    const packages: { name: string; treat1: number | null; treat2: number | null }[] = [];
    const entries = (this.retrievedPackageData?.length ? this.retrievedPackageData : this.currentPackageData) ?? [];
    entries.forEach((entry: any, index: number) => {
      const packageRows = Array.isArray(entry) ? entry.flat().filter(Boolean) : [];
      const name = Array.isArray(entry)
        ? (typeof entry[0] === 'string' && entry[0].trim()
          ? entry[0]
          : packageRows[0]?.package ?? packageRows[0]?.packageName ?? `Package ${index + 1}`)
        : entry?.name;
      if (!name || (Array.isArray(entry) && packageRows.length === 0)) {
        return;
      }
      packages.push({
        name,
        treat1: this.packageTreatTotals.p1[index] ?? null,
        treat2: this.packageTreatTotals.p2[index] ?? null,
      });
    });
    return packages;
  }

  private getCurrentFormulationSignature(): string {
    return JSON.stringify({
      baseOilData: this.baseOilData,
      vmSelectedData: this.vmSelectedData,
      ppdData: this.ppdData,
      packages: this.buildFinishedOilPackages(),
    });
  }

  private getModelInputsSignature(): string {
    return JSON.stringify({
      blend1Vm: this.sharedService.get_mod_inp_vm_value() || [],
      blend2Vm: this.sharedService.get_mod_inp_vm2_value() || []
    });
  }

  private applyRevisedShearedResults(blendNumber: number, predictions: any[]): any[] {
    const modelInputs = blendNumber === 1
      ? this.sharedService.get_mod_inp_vm_value() || []
      : this.sharedService.get_mod_inp_vm2_value() || [];
    const result = applyRevisedShearedKv100(
      predictions as ShearedPredictionRow[],
      blendNumber,
      this.vmSelectedData,
      modelInputs
    );

    if (result.warning) {
      const error = createAppError({
        category: 'business',
        code: APP_ERROR_CODES.REVISED_SHEARED_KV100_INVALID_INPUT,
        source: 'revisedShearedKv100',
        severity: 'warning',
        userMessage: getUserMessage(APP_ERROR_CODES.REVISED_SHEARED_KV100_INVALID_INPUT),
        technicalMessage: `Revised sheared KV100 calculation skipped for blend ${blendNumber}`,
        details: { blendNumber, reason: result.warning }
      });
      this.logger.warn('Revised sheared KV100 calculation skipped', {
        blendNumber,
        reason: result.warning
      });
      this.notificationFacade.showError(error);
    }

    return result.predictions;
  }

  private publishPredictionResults(
    predictions: any[],
    requestedFormulationSignature: string,
    requestedModelInputsSignature: string
  ): void {
    if (requestedFormulationSignature !== this.getCurrentFormulationSignature()
      || requestedModelInputsSignature !== this.getModelInputsSignature()) {
      return;
    }

    this.sharedService.set_prediction_results(predictions);
    this.sharedService.setpredictionvaluedata('Treat_wt_ratio_P1');
  }

  closeWeightCalculator(): void {
    this.isWeightCalculatorOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isWeightCalculatorOpen) {
      this.closeWeightCalculator();
    }
  }

  openModal2() {
    // Only opens the modal; loading is handled by the child component
    this.isModal2Open = true;
  }

  closeModals() {
    this.isModal1Open = false;
    this.isModal2Open = false;
  }

  scrollToTop() {
    const element = document.getElementById('blend-modification');
    if (element) {
      // Find the scroll container
      let scrollContainer = this.findScrollParent(element);

      if (scrollContainer) {
        // Move to the start if it is needed.
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.logger.warn('Could not find a scroll container for blend modification section');
      }
    } else {
      this.logger.warn('Blend modification element was not found');
    }
  }

  scrollToPC() {
    const element = document.getElementById('package-contents');
    if (element) {
      let scrollContainer = this.findScrollParent(element);

      if (scrollContainer) {
        // Use getBoundingClientRect for a more precise position
        const elementRect = element.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;

        // Subtract a small offset for better visibility
        scrollContainer.scrollTo({
          top: Math.max(0, relativeTop - 20),
          behavior: 'smooth'
        });
      } else {
          this.logger.warn('Could not find a scroll container for package contents section');
      }
    } else {
        this.logger.warn('Package contents element was not found');
    }
  }

  private collectDataForModal(blend: number) {
    // Gather data from the current selections
    this.modalData[blend] = {
      // selectedVM: this.getSelectedVM(),
      // selectedPackage: this.getSelectedPackage(),
      // selectedBaseOil: this.getSelectedBaseOil(),
      // ... other data
    };
  }

  // Removed: direct loading from the parent caused an empty call.


  onTableDataChange(data: any[]): void {
    this.currentPackageData = data;
    // Also publish it in SharedService so it is available for Execute Prediction
    this.sharedService.setpackagedata(data);
    // Re-validate whether prediction can be executed
    this.validatePredictionReady();
  }

  // Method to find the first parent container that has scroll
  private findScrollParent(node: HTMLElement): HTMLElement | null {
    if (!node) return null;

    // Check if this element has scroll
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }

    // If we know the ID of the specific container, we can search it directly
    const knownScrollContainerId = 'main-content-scroll-container'; // Cambia esto al ID real
    const knownContainer = document.getElementById(knownScrollContainerId);
    if (knownContainer) {
      return knownContainer;
    }

    // If it has no scroll, search recursively upwards
    return node.parentElement ? this.findScrollParent(node.parentElement) : null;
  }
}
